/**
 * PATH: packages/job-hub/src/jobs-list/filters/JobsStatusFilterControl.tsx
 * FILE_ID: JOBHUB-JOBSLIST-STATUS-FILTER-CONTROL-001
 * PURPOSE: People-Directory-shaped status Select for the Jobs primary toolbar.
 * WHY: The closed control names the same words as the status pill. Inline chips
 *   mashed strip. People uses a fixed-width Select; this is the Jobs equivalent
 *   (multi-select via portalled panel, same trigger silhouette).
 * RELATED: PeopleDirectory.jsx status Select; JobsQuickFilterChips (chip bar).
 */
import { useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { AdaptiveOverlay } from '@quotech/ui'
import { cn } from '@quotech/tenant-shared/lib/utils'
import type { JobsListAuthority } from '../jobsListAuthority'
import type { JobsListFacets } from '../gateway/JobsListGateway'
import { STATUS_BUCKETS, type StatusBucketKey } from '../jobsHubDerive'

function statusTriggerLabel(selected: StatusBucketKey[]): string {
  if (selected.length === 0 || selected.length === STATUS_BUCKETS.length) {
    return 'All statuses'
  }
  const labels = selected.map((key) => (
    STATUS_BUCKETS.find((bucket) => bucket.key === key)?.label ?? key
  ))
  return labels.join(', ')
}

export function JobsStatusFilterControl({
  authority,
  selected,
  onToggle,
}: {
  authority: JobsListAuthority<JobsListFacets>
  selected: StatusBucketKey[]
  onToggle: (key: StatusBucketKey) => void
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const counts = new Map(
    (Array.isArray(authority.value.status_buckets)
      ? authority.value.status_buckets
      : []
    ).map((option) => [option.value, option.count]),
  )
  const label = statusTriggerLabel(selected)

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label="Status"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-jobs-status-filter=""
        className={cn(
          'inline-flex h-8 min-w-[150px] max-w-[260px] shrink-0 items-center justify-between gap-2 rounded-md border border-border',
          'bg-background px-3 text-sm text-foreground shadow-xs',
          'hover:bg-accent hover:text-accent-foreground',
          open && 'bg-accent text-foreground',
        )}
      >
        <span className="truncate">{label}</span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
      </button>

      <AdaptiveOverlay
        open={open}
        onOpenChange={setOpen}
        anchorRef={triggerRef}
        mode="auto"
        autoSheetBelowPx={640}
        label="Job statuses"
        popoverWidth={220}
      >
        <div role="listbox" aria-multiselectable="true" className="flex flex-col gap-0.5 p-1">
          {STATUS_BUCKETS.map((bucket) => {
            const active = selected.includes(bucket.key)
            const count = counts.get(bucket.key)
            return (
              <button
                key={bucket.key}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => onToggle(bucket.key)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                  active
                    ? 'bg-teal-50 text-teal-800 dark:bg-teal-950/40 dark:text-teal-200'
                    : 'text-foreground hover:bg-accent hover:text-accent-foreground',
                )}
              >
                <Check
                  className={cn('h-3.5 w-3.5 shrink-0', active ? 'opacity-100' : 'opacity-0')}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1 truncate">{bucket.label}</span>
                {count !== undefined ? (
                  <span className="tabular-nums text-xs text-muted-foreground">{count}</span>
                ) : null}
              </button>
            )
          })}
          {authority.source !== 'authoritative' ? (
            <p className="px-2 pt-1 text-[10px] text-amber-700 dark:text-amber-300" role="status">
              {authority.source === 'pending' ? 'Loading portfolio counts…' : 'Loaded-page counts'}
            </p>
          ) : null}
        </div>
      </AdaptiveOverlay>
    </>
  )
}
